import { useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { Code2, Play, FileCode, Folder, RefreshCw, Loader2 } from "lucide-react";

const DEFAULT_HTML = `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Mon expérience OmniSearch</title>
  <style>
    body { font-family: ui-sans-serif, system-ui, sans-serif; padding: 40px; background:#fff5ef; color:#0a0a0a; }
    h1 { color:#ff4500; font-size: 2.5rem; letter-spacing:-0.02em; }
    button { background:#0a0a0a; color:#fff; border:0; padding:10px 16px; border-radius:999px; cursor:pointer; }
  </style>
</head>
<body>
  <h1>👋 Hello, OmniSearch</h1>
  <p>Édite ce code à gauche, le rendu se met à jour à droite.</p>
  <button onclick="alert('Bonjour depuis votre code!')">Cliquez-moi</button>
</body>
</html>`;

const TABS = [
  { id: "playground", label: "Playground", icon: Play },
  { id: "source", label: "Code source", icon: Folder },
];

export default function DevToolsPage() {
  const [tab, setTab] = useState("playground");
  // Playground
  const [code, setCode] = useState(() => localStorage.getItem("omni-devtools-code") || DEFAULT_HTML);
  const [preview, setPreview] = useState(code);
  // Source viewer
  const [tree, setTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentContent, setCurrentContent] = useState("");
  const [fileLoading, setFileLoading] = useState(false);

  const run = () => {
    setPreview(code);
    localStorage.setItem("omni-devtools-code", code);
  };
  const reset = () => { setCode(DEFAULT_HTML); setPreview(DEFAULT_HTML); };

  useEffect(() => {
    if (tab !== "source") return;
    if (tree.length === 0 && !treeLoading) {
      setTreeLoading(true);
      api.get("/dev/source/tree")
        .then(({ data }) => setTree(data.files || []))
        .finally(() => setTreeLoading(false));
    }
  }, [tab, tree.length, treeLoading]);

  const openFile = (path) => {
    setCurrentFile(path);
    setFileLoading(true);
    api.get(`/dev/source/file`, { params: { path } })
      .then(({ data }) => setCurrentContent(data.content || ""))
      .catch(() => setCurrentContent("// Erreur de lecture"))
      .finally(() => setFileLoading(false));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="devtools-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight flex items-center gap-2">
            <Code2 size={24} /> Outils de développement
          </h1>
          <div className="flex items-center gap-3 text-sm">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`devtools-tab-${id}`}
                className="omni-tab inline-flex items-center gap-2"
                data-active={tab === id}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-6">
          Sandbox sécurisé. Le code que vous écrivez tourne dans une iframe isolée et ne touche pas au site réel.
        </p>

        {tab === "playground" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-300px)] min-h-[500px]">
            <div className="rounded-lg border border-[var(--omni-border)] flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-xs flex items-center justify-between">
                <span className="font-mono">index.html</span>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={reset} data-testid="devtools-reset-btn"><RefreshCw size={13} className="mr-1" />Reset</Button>
                  <Button size="sm" onClick={run} className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="devtools-run-btn">
                    <Play size={13} className="mr-1" /> Exécuter
                  </Button>
                </div>
              </div>
              <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                spellCheck="false"
                className="flex-1 w-full p-4 font-mono text-xs leading-relaxed outline-none resize-none bg-[#0a0a0a] text-[#d4d4d4] selection:bg-[var(--omni-accent)]/30"
                data-testid="devtools-editor"
              />
            </div>
            <div className="rounded-lg border border-[var(--omni-border)] flex flex-col overflow-hidden">
              <div className="px-3 py-2 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-xs font-mono">Aperçu (sandboxed)</div>
              <iframe
                title="preview"
                srcDoc={preview}
                sandbox="allow-scripts allow-modals"
                className="flex-1 w-full bg-white"
                data-testid="devtools-preview"
              />
            </div>
          </div>
        )}

        {tab === "source" && (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-300px)] min-h-[500px]">
            <div className="border border-[var(--omni-border)] rounded-lg overflow-y-auto">
              <div className="px-3 py-2 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-xs font-mono">frontend/src</div>
              {treeLoading && <div className="p-4 text-gray-500 text-sm"><Loader2 className="inline animate-spin mr-2" size={14} />Chargement…</div>}
              <ul className="text-xs font-mono py-1">
                {tree.map((f) => (
                  <li key={f.path}>
                    <button
                      onClick={() => openFile(f.path)}
                      data-testid={`devtools-file-${f.path.replace(/[^a-z0-9]+/gi, "-")}`}
                      className={`w-full text-left px-3 py-1.5 truncate hover:bg-[var(--omni-surface)] ${currentFile === f.path ? "bg-[var(--omni-surface)] font-semibold" : ""}`}
                      title={f.path}
                    >
                      <FileCode className="inline mr-1 text-gray-400" size={11} /> {f.path}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[var(--omni-border)] rounded-lg overflow-hidden flex flex-col">
              <div className="px-3 py-2 border-b border-[var(--omni-border)] bg-[var(--omni-surface)] text-xs font-mono flex items-center justify-between">
                <span>{currentFile || "Sélectionnez un fichier"}</span>
                <span className="text-gray-400">lecture seule</span>
              </div>
              <div className="flex-1 overflow-auto bg-[#0a0a0a] text-[#d4d4d4] font-mono text-xs leading-relaxed">
                {fileLoading ? (
                  <div className="p-4 text-gray-400"><Loader2 className="inline animate-spin mr-2" size={14} />Lecture…</div>
                ) : currentFile ? (
                  <pre className="p-4 whitespace-pre">{currentContent}</pre>
                ) : (
                  <div className="p-4 text-gray-500">Cliquez sur un fichier à gauche.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
